import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { OptionType } from '@nxsys/components/AppSelectSync';
import {
  useGetPensionContributionReportList,
  useMarkAsSubmittedPension,
  useResubmitPension,
} from '@nxsys/hooks/api/report';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetPensionContributionReportListParams } from '@nxsys/store/slices/report/types';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoneyExport } from '@nxsys/utils/number-helper';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PensionContributionFilterModal, {
  PensionContributionFilterValues,
} from '../components/PensionContributionFilterModal';
import { getPensionContributionReportColumns } from './columns';
import {
  PENSION_CONTRIBUTION_TAB,
  PensionContributionTabRef,
} from './PensionContributionReport';
import AppLoader from '@nxsys/components/AppLoader';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

export type SubmittedPensionContributionTabProps = {
  pensionContributionReportRef: React.RefObject<PensionContributionTabRef>;
  pensionTabType: PENSION_CONTRIBUTION_TAB;
};

const SubmittedPensionContributionTab: React.FC<
  SubmittedPensionContributionTabProps
> = ({ pensionContributionReportRef, pensionTabType }) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch();
  const { pensionContributionReportList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } =
    pensionContributionReportList;
  const { payrollCountry, isBureauEmployer, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IPensionSubmission[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const {
    run: getPensionContributionReportList,
    loading: getPensionContributionReportListLoading,
  } = useGetPensionContributionReportList();

  useImperativeHandle(pensionContributionReportRef, () => ({
    exportSelected: handleExportSelected,
    exportAll: handleExportAll,
    exportErrorsSelected: handleExportErrorsSelected,
    exportAllErrors: handleExportAllErrors,
    resubmit: handleResubmitSelected,
    markAsSubmitted: handleMarkAsSubmittedSelected,
  }));

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, []);

  const handleFetchData = (params: GetPensionContributionReportListParams) => {
    const newParams = {
      ...params,
      ...(pensionTabType === PENSION_CONTRIBUTION_TAB.FAILED_SUBMISSION
        ? {
            pensionsError: 'TRUE',
          }
        : {}),
    };

    newParams.companyRefs = resolveFilterCompanyIds(
      params.companyRefs,
      isBureauEmployer,
      user?.companyUserIds,
    );

    dispatch(reportActions.getPensionContributionReportList(newParams));
  };

  const refetch = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: PensionContributionFilterValues) => {
    const params: GetPensionContributionReportListParams = {
      ...queryParams,
      companyRefs: values?.companies?.map((com: OptionType) => com.value),
      payFrequency: values.payFrequency,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
    };
    handleFetchData(params);
  };

  const handleExport = (data: IPensionSubmission[]) => {
    const dataExport = data.map((item) => {
      const {
        taxPeriod,
        currentYear,
        taxYear,
        subcontractorId,
        subcontractor,
        agency,
        company,
        grossForPension,
        eePension,
        erPension,
        acv,
        contributedPension,
        payroll,
      } = item;
      return {
        taxPeriod: taxPeriod || '',
        taxYear: taxYear || currentYear || '',
        employeeId: subcontractorId,
        employeeName:
          subcontractor?.fullName ||
          `${subcontractor?.forename || ''} ${subcontractor?.surname || ''}`,
        agencyRef: `${agency?.agencyPrefix || '___'}/${
          subcontractor?.agencyRef || ''
        }`,
        paymentCompany: company?.companyName || '',
        grossForPension: formatMoneyExport(
          grossForPension,
          showCurrencyByCountry(payrollCountry),
        ),
        employeePension: formatMoneyExport(
          eePension,
          showCurrencyByCountry(payrollCountry),
        ),
        avcEmployee: formatMoneyExport(
          payroll?.payeeUmbrellaValue?.avcEEPension,
          showCurrencyByCountry(payrollCountry),
        ),
        employersPension: formatMoneyExport(
          erPension,
          showCurrencyByCountry(payrollCountry),
        ),
        avcEmployer: formatMoneyExport(
          payroll?.payeeUmbrellaValue?.avcERPension,
          showCurrencyByCountry(payrollCountry),
        ),
        additionalVoluntaryContribution: formatMoneyExport(
          acv,
          showCurrencyByCountry(payrollCountry),
        ),
        totalContribution: formatMoneyExport(
          contributedPension,
          showCurrencyByCountry(payrollCountry),
        ),
      };
    });

    const headers = [
      'Tax Period',
      'Tax Year',
      'Employee ID',
      'Employee Name',
      'Agency Reference',
      'Payment Company',
      'Gross for Pension',
      'Employee Contribution',
      'AVC Employee',
      'Employer Contribution',
      'AVC Employer',
      'Additional Voluntary Contribution',
      'Total Contribution',
    ];

    const csvExporter = new ExportToCsv({
      filename:
        `Pension-contributions-report-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    getPensionContributionReportList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const handleExportErrors = (data: IPensionSubmission[]) => {
    const dataExport = data.map((item) => {
      const {
        taxPeriod,
        taxYear,
        subcontractorId,
        subcontractor,
        agency,
        company,
        grossForPension,
        eePension,
        erPension,
        acv,
        contributedPension,
        contributePensionError,
      } = item;
      return {
        taxPeriod: taxPeriod || '',
        taxYear: taxYear || '',
        employeeId: subcontractorId,
        employeeName:
          subcontractor?.fullName ||
          `${subcontractor?.forename || ''} ${subcontractor?.surname || ''}`,
        agencyRef: `${agency?.agencyPrefix || '___'}/${
          subcontractor?.agencyRef || ''
        }`,
        paymentCompany: company?.companyName || '',
        grossForPension: formatMoneyExport(
          grossForPension,
          showCurrencyByCountry(payrollCountry),
        ),
        employeePension: formatMoneyExport(
          eePension,
          showCurrencyByCountry(payrollCountry),
        ),
        employersPension: formatMoneyExport(
          erPension,
          showCurrencyByCountry(payrollCountry),
        ),
        additionalVoluntaryContribution: formatMoneyExport(
          acv,
          showCurrencyByCountry(payrollCountry),
        ),
        totalContribution: formatMoneyExport(
          contributedPension,
          showCurrencyByCountry(payrollCountry),
        ),
        contributePensionError: contributePensionError || '',
      };
    });

    const headers = [
      'Tax Period',
      'Tax Year',
      'Employee ID',
      'Employee Name',
      'Agency Reference',
      'Payment Company',
      'Gross for Pension',
      'Employee Contribution',
      'Employer Contribution',
      'Additional Voluntary Contribution',
      'Total Contribution',
      'Error',
    ];

    const csvExporter = new ExportToCsv({
      filename:
        `Pension-errors-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportErrorsSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExportErrors(selectedRows);
  };

  const handleExportAllErrors = () => {
    if (!pagination.totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    getPensionContributionReportList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
      pensionsError: 'TRUE',
    }).then((res) => {
      handleExportErrors(res.data);
    });
  };

  const { run: resubmitPension, loading: resubmitPensionLoading } =
    useResubmitPension();
  const handleResubmitSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    resubmitPension({
      ids: selectedRows.map((item) => item.id),
    }).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.resubmitSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: res.message,
        });
      }
      refetch();
    });
  };

  const {
    run: markAsSubmittedPension,
    loading: markAsSubmittedPensionLoading,
  } = useMarkAsSubmittedPension();
  const handleMarkAsSubmittedSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    markAsSubmittedPension({
      ids: selectedRows.map((item) => item.id),
    }).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.markAsSubmittedSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content:
            res.message || messages['report.pension.markAsSubmittedError'],
        });
      }
      refetch();
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.payFrequency;

  return (
    <>
      {(getPensionContributionReportListLoading ||
        resubmitPensionLoading ||
        markAsSubmittedPensionLoading) && <AppLoader />}
      <AppsHeader>
        <div className="flex justify-between flex-wrap w-full gap-4">
          <div className="flex flex-wrap gap-4">
            <AppInputSearch onChange={handleSearch} />
            <AppMenuFilter
              options={[]}
              onChange={() => {}}
              onClick={() => setIsOpenFilterModal(true)}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <TaxYearSelect
              initValue={currentTaxYear}
              onFilter={(value) =>
                handleFetchData({ ...queryParams, taxYear: value.taxYear })
              }
            />
          </div>
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2700, y: 700 }}
          columns={getPensionContributionReportColumns({
            messages,
            payrollCountry,
            pensionTabType,
            isBureau,
          })}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>
      <PensionContributionFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        isBureau={isBureau}
      />
    </>
  );
};

export default SubmittedPensionContributionTab;
